import React, { type CSSProperties } from "react";
import { ArrowRight, PlayCircle } from "lucide-react";

export type YouTubePlaylistLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    style?: CSSProperties;
    "aria-label"?: string;
    [key: string]: any;
}>;

export type YouTubePlaylistImage = {
    src?: string;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    objectPosition?: string;
};

export type YouTubePlaylistVideo = {
    id?: string | number;
    videoId?: string;
    title: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    thumbnail?: YouTubePlaylistImage;
    image?: YouTubePlaylistImage;
    thumbnailUrl?: string;
    imageUrl?: string;
    imageAlt?: string;
    imagePosition?: string;
    duration?: React.ReactNode;
};

export type YouTubePlaylistItem = {
    id?: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    href?: string;
    ctaLabel?: React.ReactNode;
    ctaAriaLabel?: string;
    image?: YouTubePlaylistImage;
    imageUrl?: string;
    imageAlt?: string;
    imagePosition?: string;
    videos?: YouTubePlaylistVideo[];
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
};

export type YouTubePlaylistColorToken =
    | "background"
    | "surface"
    | "surfaceElevated"
    | "border"
    | "text"
    | "softText"
    | "mutedText"
    | "accent"
    | "accentSoft";

export type YouTubePlaylistColors = Partial<Record<YouTubePlaylistColorToken, string>>;

export type YouTubePlaylistProps = {
    playlist?: YouTubePlaylistItem;
    playlists?: YouTubePlaylistItem[];
    Link?: YouTubePlaylistLinkComponent;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    titleComponent?: "h1" | "h2" | "h3";
    description?: React.ReactNode;
    viewAllHref?: string;
    viewAllLabel?: React.ReactNode;
    viewAllAriaLabel?: string;
    playlistCtaLabel?: React.ReactNode;
    showVideoTitles?: boolean;
    colors?: YouTubePlaylistColors;
    colours?: YouTubePlaylistColors;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type YouTubePlaylistStyle = CSSProperties &
    Record<`--youtube-playlists-${string}`, string | number | undefined>;

type YouTubePlaylistCardStyle = CSSProperties &
    Record<`--youtube-playlist-card-${string}`, string | number | undefined>;

const defaultColors: Required<YouTubePlaylistColors> = {
    background: "#070707",
    surface: "#101010",
    surfaceElevated: "#151515",
    border: "rgba(255, 255, 255, 0.12)",
    text: "#ffffff",
    softText: "rgba(255, 255, 255, 0.76)",
    mutedText: "rgba(255, 255, 255, 0.58)",
    accent: "#ef332c",
    accentSoft: "rgba(239, 51, 44, 0.16)",
};

const DefaultLink: YouTubePlaylistLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const youtubePlaylistCss = `
.youtube-playlists {
  --youtube-playlists-bg: ${defaultColors.background};
  --youtube-playlists-surface: ${defaultColors.surface};
  --youtube-playlists-surface-elevated: ${defaultColors.surfaceElevated};
  --youtube-playlists-border: ${defaultColors.border};
  --youtube-playlists-text: ${defaultColors.text};
  --youtube-playlists-soft-text: ${defaultColors.softText};
  --youtube-playlists-muted-text: ${defaultColors.mutedText};
  --youtube-playlists-accent: ${defaultColors.accent};
  --youtube-playlists-accent-soft: ${defaultColors.accentSoft};
  --youtube-playlists-max-width: 1440px;
  width: 100%;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, color-mix(in srgb, var(--youtube-playlists-accent) 12%, transparent) 0, transparent 30%),
    var(--youtube-playlists-bg);
  color: var(--youtube-playlists-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.youtube-playlists,
.youtube-playlists * {
  box-sizing: border-box;
}
.youtube-playlists-shell {
  width: 100%;
  max-width: var(--youtube-playlists-max-width);
  margin: 0 auto;
  padding: clamp(32px, 5vw, 64px);
}
.youtube-playlists-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
}
.youtube-playlists-heading {
  min-width: 0;
}
.youtube-playlists-eyebrow {
  margin: 0 0 8px;
  color: var(--youtube-playlists-accent);
  font-size: 13px;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}
.youtube-playlists-title {
  margin: 0;
  color: var(--youtube-playlists-text);
  font-size: clamp(28px, 4vw, 44px);
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}
.youtube-playlists-title-accent {
  color: var(--youtube-playlists-accent);
}
.youtube-playlists-description {
  max-width: 720px;
  margin: 12px 0 0;
  color: var(--youtube-playlists-soft-text);
  font-size: 16px;
  line-height: 1.55;
  letter-spacing: 0;
}
.youtube-playlists-title-rule {
  display: block;
  width: 72px;
  height: 4px;
  margin-top: 20px;
  background: var(--youtube-playlists-accent);
}
.youtube-playlists-view-all,
.youtube-playlist-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  max-width: 100%;
  color: var(--youtube-playlists-text);
  font-size: 14px;
  line-height: 1.1;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: uppercase;
  text-decoration: none;
  transition:
    border-color 160ms ease,
    color 160ms ease,
    background-color 160ms ease,
    transform 160ms ease;
}
.youtube-playlists-view-all {
  flex: 0 0 auto;
  min-height: 48px;
  padding: 0 20px;
  border: 1px solid var(--youtube-playlists-border);
  border-radius: 8px;
  background: color-mix(in srgb, var(--youtube-playlists-surface) 62%, transparent);
}
.youtube-playlists-view-all:hover,
.youtube-playlists-view-all:focus-visible,
.youtube-playlist-cta:hover,
.youtube-playlist-cta:focus-visible {
  color: var(--youtube-playlists-accent);
}
.youtube-playlists-view-all:hover,
.youtube-playlists-view-all:focus-visible {
  border-color: color-mix(in srgb, var(--youtube-playlists-accent) 68%, var(--youtube-playlists-border));
  background: var(--youtube-playlists-accent-soft);
}
.youtube-playlists-view-all:focus-visible,
.youtube-playlist-cta:focus-visible,
.youtube-playlist-cover-link:focus-visible,
.youtube-playlist-video-link:focus-visible {
  outline: 2px solid var(--youtube-playlists-accent);
  outline-offset: 3px;
}
.youtube-playlists-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  color: var(--youtube-playlists-accent);
}
.youtube-playlists-list {
  display: grid;
  gap: 28px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.youtube-playlist-card {
  --youtube-playlist-card-accent: var(--youtube-playlists-accent);
  --youtube-playlist-card-accent-soft: color-mix(in srgb, var(--youtube-playlist-card-accent) 14%, transparent);
  display: grid;
  grid-template-columns: minmax(280px, 0.58fr) minmax(0, 1fr);
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--youtube-playlists-border);
  border-radius: 8px;
  background:
    linear-gradient(135deg, var(--youtube-playlist-card-accent-soft), transparent 34%),
    linear-gradient(90deg, var(--youtube-playlists-surface-elevated), var(--youtube-playlists-surface));
}
.youtube-playlist-cover,
.youtube-playlist-cover-link {
  display: block;
  min-width: 0;
  min-height: 256px;
  color: inherit;
  text-decoration: none;
}
.youtube-playlist-cover-media {
  width: 100%;
  height: 100%;
  min-height: 256px;
  display: block;
  object-fit: cover;
  object-position: center;
  background: var(--youtube-playlists-surface-elevated);
  filter: saturate(0.86);
}
.youtube-playlist-image-fallback {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 256px;
  align-items: center;
  justify-content: center;
  color: var(--youtube-playlist-card-accent);
  background:
    linear-gradient(135deg, var(--youtube-playlist-card-accent-soft), transparent 42%),
    var(--youtube-playlists-surface-elevated);
}
.youtube-playlist-content {
  min-width: 0;
  padding: clamp(22px, 3vw, 34px);
}
.youtube-playlist-title {
  margin: 0;
  color: var(--youtube-playlists-text);
  font-size: clamp(22px, 3vw, 32px);
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}
.youtube-playlist-description {
  max-width: 760px;
  margin: 10px 0 0;
  color: var(--youtube-playlists-soft-text);
  font-size: 17px;
  line-height: 1.45;
  letter-spacing: 0;
}
.youtube-playlist-cta {
  width: fit-content;
  margin-top: 18px;
  color: var(--youtube-playlist-card-accent);
}
.youtube-playlist-cta .youtube-playlists-icon {
  color: currentColor;
}
.youtube-playlist-videos {
  display: flex;
  gap: 12px;
  min-width: 0;
  margin: 28px 0 0;
  padding: 0 0 10px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-padding-inline: 2px;
  scroll-snap-type: x proximity;
  scrollbar-color: var(--youtube-playlist-card-accent) transparent;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}
.youtube-playlist-videos::-webkit-scrollbar {
  height: 8px;
}
.youtube-playlist-videos::-webkit-scrollbar-track {
  background: transparent;
}
.youtube-playlist-videos::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: color-mix(in srgb, var(--youtube-playlist-card-accent) 72%, transparent);
}
.youtube-playlist-video {
  flex: 0 0 clamp(160px, 18vw, 240px);
  min-width: 0;
  scroll-snap-align: start;
  list-style: none;
}
.youtube-playlist-video-link,
.youtube-playlist-video-static {
  display: block;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}
.youtube-playlist-video-media {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--youtube-playlists-border);
  border-radius: 7px;
  background: var(--youtube-playlists-surface-elevated);
}
.youtube-playlist-video-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 180ms ease, filter 180ms ease;
}
.youtube-playlist-video-link:hover .youtube-playlist-video-image,
.youtube-playlist-video-link:focus-visible .youtube-playlist-video-image {
  transform: scale(1.035);
  filter: saturate(1.08);
}
.youtube-playlist-video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.2));
  opacity: 0;
  transition: opacity 160ms ease;
}
.youtube-playlist-video-link:hover .youtube-playlist-video-overlay,
.youtube-playlist-video-link:focus-visible .youtube-playlist-video-overlay {
  opacity: 1;
}
.youtube-playlist-video-play {
  width: 34px;
  height: 34px;
  color: var(--youtube-playlist-card-accent);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.45));
}
.youtube-playlist-duration {
  position: absolute;
  right: 8px;
  bottom: 8px;
  max-width: calc(100% - 16px);
  padding: 4px 7px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.78);
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
}
.youtube-playlist-video-title {
  display: block;
  margin-top: 9px;
  color: var(--youtube-playlists-soft-text);
  font-size: 13px;
  line-height: 1.35;
  font-weight: 700;
  letter-spacing: 0;
}
.youtube-playlist-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (max-width: 900px) {
  .youtube-playlist-card {
    grid-template-columns: 1fr;
  }
  .youtube-playlist-cover,
  .youtube-playlist-cover-link,
  .youtube-playlist-cover-media,
  .youtube-playlist-image-fallback {
    min-height: 0;
  }
  .youtube-playlist-cover-media,
  .youtube-playlist-image-fallback {
    aspect-ratio: 16 / 9;
  }
}
@media (max-width: 700px) {
  .youtube-playlists-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .youtube-playlists-view-all {
    min-height: 44px;
  }
  .youtube-playlist-video {
    flex-basis: min(72vw, 240px);
  }
}
@media (max-width: 520px) {
  .youtube-playlists-shell {
    padding: 28px 16px;
  }
  .youtube-playlists-title {
    font-size: 28px;
  }
  .youtube-playlist-content {
    padding: 20px;
  }
  .youtube-playlist-title {
    font-size: 22px;
  }
  .youtube-playlist-description {
    font-size: 15px;
  }
}
`;

const joinClassNames = (...classNames: Array<string | false | null | undefined>) =>
    classNames.filter(Boolean).join(" ");

const toCssSize = (value?: number | string) => {
    if (typeof value === "number") {
        return `${value}px`;
    }

    return value;
};

const getText = (value: React.ReactNode, fallback: string) => {
    if (typeof value === "string" || typeof value === "number") {
        return String(value);
    }

    return fallback;
};

const getImageSrc = (image?: YouTubePlaylistImage) =>
    image?.src || image?.url || image?.original || image?.thumbnail;

const getImageAlt = (image: YouTubePlaylistImage | undefined, fallback: string) =>
    image?.alt || image?.title || fallback;

const getVideoImageSrc = (video?: YouTubePlaylistVideo) => {
    if (!video) {
        return undefined;
    }

    return (
        video.thumbnailUrl ||
        video.imageUrl ||
        getImageSrc(video.thumbnail) ||
        getImageSrc(video.image) ||
        (video.videoId ? `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg` : undefined)
    );
};

const YouTubePlaylist = ({
    playlist,
    playlists,
    Link = DefaultLink,
    eyebrow,
    title = (
        <>
            <span className="youtube-playlists-title-accent">YouTube</span> Playlists
        </>
    ),
    titleComponent = "h2",
    description,
    viewAllHref,
    viewAllLabel = "View all playlists",
    viewAllAriaLabel,
    playlistCtaLabel = "View playlist",
    showVideoTitles = false,
    colors,
    colours,
    accentColor,
    accentColour,
    accentSoftColor,
    accentSoftColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    className,
    style,
    ariaLabel,
}: YouTubePlaylistProps) => {
    const items = playlists || (playlist ? [playlist] : []);
    const colorValues = { ...colors, ...colours };
    const TitleTag = titleComponent;
    const rootStyle: YouTubePlaylistStyle = {
        "--youtube-playlists-bg":
            backgroundColor || backgroundColour || colorValues.background || defaultColors.background,
        "--youtube-playlists-surface":
            surfaceColor || surfaceColour || colorValues.surface || defaultColors.surface,
        "--youtube-playlists-surface-elevated":
            colorValues.surfaceElevated || defaultColors.surfaceElevated,
        "--youtube-playlists-border": colorValues.border || defaultColors.border,
        "--youtube-playlists-text": textColor || textColour || colorValues.text || defaultColors.text,
        "--youtube-playlists-soft-text": colorValues.softText || defaultColors.softText,
        "--youtube-playlists-muted-text": colorValues.mutedText || defaultColors.mutedText,
        "--youtube-playlists-accent":
            accentColor || accentColour || colorValues.accent || defaultColors.accent,
        "--youtube-playlists-accent-soft":
            accentSoftColor || accentSoftColour || colorValues.accentSoft || defaultColors.accentSoft,
        "--youtube-playlists-max-width": toCssSize(maxWidth) || "1440px",
        ...style,
    };
    const hasHeader = Boolean(eyebrow || title || description || viewAllHref);

    return (
        <section
            className={joinClassNames("youtube-playlists", className)}
            style={rootStyle}
            aria-label={ariaLabel || getText(title, "YouTube playlists")}
        >
            <style>{youtubePlaylistCss}</style>

            <div className="youtube-playlists-shell">
                {hasHeader && (
                    <div className="youtube-playlists-header">
                        <div className="youtube-playlists-heading">
                            {eyebrow && <p className="youtube-playlists-eyebrow">{eyebrow}</p>}
                            {title && <TitleTag className="youtube-playlists-title">{title}</TitleTag>}
                            <span className="youtube-playlists-title-rule" aria-hidden="true" />
                            {description && (
                                <p className="youtube-playlists-description">{description}</p>
                            )}
                        </div>

                        {viewAllHref && (
                            <Link
                                href={viewAllHref}
                                className="youtube-playlists-view-all"
                                aria-label={viewAllAriaLabel}
                            >
                                <span>{viewAllLabel}</span>
                                <ArrowRight className="youtube-playlists-icon" aria-hidden="true" />
                            </Link>
                        )}
                    </div>
                )}

                <ul className="youtube-playlists-list" role="list">
                    {items.map((item, index) => {
                        const videos = item.videos || [];
                        const playlistTitle = getText(item.title, `Playlist ${index + 1}`);
                        const coverImage =
                            item.imageUrl || getImageSrc(item.image) || getVideoImageSrc(videos[0]);
                        const coverAlt =
                            item.imageAlt ||
                            getImageAlt(item.image, `${playlistTitle} playlist cover`);
                        const itemAccent = item.accentColor || item.accentColour;
                        const itemAccentSoft = item.accentSoftColor || item.accentSoftColour;
                        const cardStyle: YouTubePlaylistCardStyle = {
                            "--youtube-playlist-card-accent":
                                itemAccent || "var(--youtube-playlists-accent)",
                            "--youtube-playlist-card-accent-soft":
                                itemAccentSoft ||
                                "color-mix(in srgb, var(--youtube-playlist-card-accent) 14%, transparent)",
                        };
                        const itemKey = item.id ?? item.href ?? playlistTitle;
                        const coverContent = coverImage ? (
                            <img
                                className="youtube-playlist-cover-media"
                                src={coverImage}
                                alt={coverAlt}
                                loading="lazy"
                                decoding="async"
                                style={{
                                    objectPosition:
                                        item.imagePosition || item.image?.objectPosition,
                                }}
                            />
                        ) : (
                            <div className="youtube-playlist-image-fallback" aria-hidden="true">
                                <PlayCircle size={56} strokeWidth={1.5} />
                            </div>
                        );

                        return (
                            <li key={itemKey} className="youtube-playlist-card" style={cardStyle}>
                                {item.href ? (
                                    <Link
                                        href={item.href}
                                        className="youtube-playlist-cover-link"
                                        aria-label={item.ctaAriaLabel || `View ${playlistTitle}`}
                                    >
                                        {coverContent}
                                    </Link>
                                ) : (
                                    <div className="youtube-playlist-cover">{coverContent}</div>
                                )}

                                <article className="youtube-playlist-content">
                                    <h3 className="youtube-playlist-title">{item.title}</h3>
                                    {item.description && (
                                        <p className="youtube-playlist-description">
                                            {item.description}
                                        </p>
                                    )}

                                    {item.href && (
                                        <Link
                                            href={item.href}
                                            className="youtube-playlist-cta"
                                            aria-label={item.ctaAriaLabel || `View ${playlistTitle}`}
                                        >
                                            <span>{item.ctaLabel || playlistCtaLabel}</span>
                                            <ArrowRight className="youtube-playlists-icon" aria-hidden="true" />
                                        </Link>
                                    )}

                                    {videos.length > 0 && (
                                        <ul
                                            className="youtube-playlist-videos"
                                            role="list"
                                            aria-label={`${playlistTitle} videos`}
                                        >
                                            {videos.map((video, videoIndex) => {
                                                const videoTitle = getText(
                                                    video.title,
                                                    `Video ${videoIndex + 1}`,
                                                );
                                                const videoImage =
                                                    getVideoImageSrc(video) || getVideoImageSrc(videos[0]);
                                                const videoAlt =
                                                    video.imageAlt ||
                                                    getImageAlt(
                                                        video.thumbnail || video.image,
                                                        `${videoTitle} thumbnail`,
                                                    );
                                                const videoContent = (
                                                    <>
                                                        <span className="youtube-playlist-video-media">
                                                            {videoImage ? (
                                                                <img
                                                                    className="youtube-playlist-video-image"
                                                                    src={videoImage}
                                                                    alt={videoAlt}
                                                                    loading="lazy"
                                                                    decoding="async"
                                                                    style={{
                                                                        objectPosition:
                                                                            video.imagePosition ||
                                                                            video.thumbnail?.objectPosition ||
                                                                            video.image?.objectPosition,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span
                                                                    className="youtube-playlist-image-fallback"
                                                                    aria-hidden="true"
                                                                >
                                                                    <PlayCircle size={36} strokeWidth={1.6} />
                                                                </span>
                                                            )}
                                                            {video.href && (
                                                                <span
                                                                    className="youtube-playlist-video-overlay"
                                                                    aria-hidden="true"
                                                                >
                                                                    <PlayCircle
                                                                        className="youtube-playlist-video-play"
                                                                        strokeWidth={1.8}
                                                                    />
                                                                </span>
                                                            )}
                                                            {video.duration && (
                                                                <span className="youtube-playlist-duration">
                                                                    {video.duration}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span
                                                            className={
                                                                showVideoTitles
                                                                    ? "youtube-playlist-video-title"
                                                                    : "youtube-playlist-sr-only"
                                                            }
                                                        >
                                                            {video.title}
                                                        </span>
                                                    </>
                                                );

                                                return (
                                                    <li
                                                        className="youtube-playlist-video"
                                                        key={video.id ?? video.href ?? video.videoId ?? videoIndex}
                                                    >
                                                        {video.href ? (
                                                            <Link
                                                                href={video.href}
                                                                className="youtube-playlist-video-link"
                                                                aria-label={video.ariaLabel || `Watch ${videoTitle}`}
                                                            >
                                                                {videoContent}
                                                            </Link>
                                                        ) : (
                                                            <div className="youtube-playlist-video-static">
                                                                {videoContent}
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </article>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
};

export default YouTubePlaylist;
